/**
 * CartStateManager - Single Source of Truth for Shopify Cart
 *
 * Manages cart state synchronization between cart page and side cart drawer.
 * Prevents race conditions by serializing all cart API requests.
 *
 * Architecture:
 * - Browser Cookie (Shopify Cart) ← Single source of truth
 * - CartStateManager (Coordinator) ← Serializes requests, caches state
 * - Cart Page + Side Cart (Views) ← Subscribe to state changes
 */

class CartStateManager {
  constructor() {
    // Singleton pattern - ensure only one instance exists
    if (CartStateManager.instance) {
      return CartStateManager.instance;
    }

    // Initial cart state (will be populated from server)
    this.state = {
      items: [],
      item_count: 0,
      total_price: 0,
      original_total_price: 0,
      items_subtotal_price: 0,
      total_discount: 0,
      currency: 'USD',
      requires_shipping: true,
      token: null
    };

    // Request queue for serial execution (prevents race conditions)
    this.requestQueue = [];
    this.isProcessing = false;

    // Observer pattern - components subscribe to state changes
    this.subscribers = new Set();

    // Multi-tab synchronization via BroadcastChannel
    // Fallback to localStorage events for older browsers
    this.setupMultiTabSync();

    CartStateManager.instance = this;
  }

  /**
   * Setup multi-tab synchronization
   */
  setupMultiTabSync() {
    // Try BroadcastChannel API (modern browsers)
    if (typeof BroadcastChannel !== 'undefined') {
      this.broadcastChannel = new BroadcastChannel('bartesian-cart-sync');

      this.broadcastChannel.onmessage = (event) => {
        if (event.data.type === 'cart-update') {
          // Update state without broadcasting back (prevent infinite loop)
          this.setState(event.data.state, true);
        }
      };
    } else {
      // Fallback to localStorage events for older browsers
      window.addEventListener('storage', (event) => {
        if (event.key === 'bartesian-cart-state' && event.newValue) {
          try {
            const newState = JSON.parse(event.newValue);
            this.setState(newState, true);
          } catch (error) {
            console.error('CartStateManager: Failed to parse localStorage cart state', error);
          }
        }
      });
    }
  }

  /**
   * Initialize cart state from Shopify server
   * Should be called once on page load
   */
  async init() {
    try {
      const cartData = await this.fetchCart();
      this.setState(cartData);
      return cartData;
    } catch (error) {
      console.error('CartStateManager: Failed to initialize cart', error);
      throw error;
    }
  }

  /**
   * Fetch current cart state from Shopify
   * @returns {Promise<Object>} Cart data from server
   */
  async fetchCart() {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart.js');
      const sideCart = document.querySelector("side-cart");

      if (!response.ok) {
        if (response.status === 429 && sideCart) {
          sideCart.showErrorPopup('Too many requests detected. Please wait a moment and try again. This page will refresh automatically in 3 seconds.');
          setTimeout(() => {
            window.location.reload();
          }, 3000);
          throw new Error('Too many requests detected. Please wait a moment and try again. This page will refresh automatically in 3 seconds.');
        }
        throw new Error(`Cart fetch failed: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('CartStateManager: fetchCart error', error);
      throw error;
    }
  }

  /**
   * Queue a cart mutation request for serial execution
   * This prevents race conditions by ensuring requests execute one at a time
   *
   * @param {Function} requestFn - Async function that makes the cart API request
   * @param {Number} retries - Number of retry attempts on failure
   * @returns {Promise} Resolves when request completes
   */
  async queueRequest(requestFn, retries = 2) {
    return new Promise((resolve, reject) => {
      this.requestQueue.push({
        requestFn,
        resolve,
        reject,
        retries
      });

      // Start processing if not already running
      this.processQueue();
    });
  }

  /**
   * Process queued requests serially
   * Only one request executes at a time to prevent race conditions
   */
  async processQueue() {
    // Already processing or queue is empty
    if (this.isProcessing || this.requestQueue.length === 0) {
      return;
    }

    this.isProcessing = true;
    const request = this.requestQueue.shift();
    const { requestFn, resolve, reject, retries } = request;

    try {
      // Execute the cart API request
      const result = await requestFn();

      // Always fetch fresh state from server after mutation
      // The Shopify cookie is the source of truth
      const freshState = await this.fetchCart();
      this.setState(freshState);

      resolve(result);
    } catch (error) {
      console.error('CartStateManager: Request failed', error);

      // Retry logic. A cart rule rejection (checkout validation) is a
      // deliberate refusal, not a transient failure — retrying it only delays
      // the message the shopper needs to see.
      if (retries > 0 && !error.isCartRuleError) {
        console.log(`CartStateManager: Retrying request (${retries} attempts left)`);
        request.retries = retries - 1;
        this.requestQueue.unshift(request); // Put back at front of queue
      } else {
        // Show user-friendly error
        this.dispatchError(error.message || 'There was an error while updating your cart. Please try again.');
        reject(error);
        
        // Fetch fresh state to ensure consistency
        try {
          const freshState = await this.fetchCart();
          this.setState(freshState);
        } catch (fetchError) {
          console.error('CartStateManager: Failed to fetch cart after error', fetchError);
        }
      }
    } finally {
      this.isProcessing = false;
      // Process next request in queue
      this.processQueue();
    }
  }

  /**
   * Update cart state and notify all subscribers
   *
   * @param {Object} newState - New cart state from server
   * @param {Boolean} skipBroadcast - Skip multi-tab broadcast (used when receiving from another tab)
   */
  setState(newState, skipBroadcast = false) {
    const oldState = { ...this.state };
    this.state = { ...this.state, ...newState };

    // Notify all subscribed components
    this.subscribers.forEach(callback => {
      try {
        callback(this.state, oldState);
      } catch (error) {
        console.error('CartStateManager: Subscriber callback error', error);
      }
    });

    // Broadcast to other tabs (if not already from another tab)
    if (!skipBroadcast) {
      if (this.broadcastChannel) {
        // Modern browsers
        this.broadcastChannel.postMessage({
          type: 'cart-update',
          state: this.state
        });
      } else {
        // Fallback for older browsers
        try {
          localStorage.setItem('bartesian-cart-state', JSON.stringify(this.state));
        } catch (error) {
          console.error('CartStateManager: Failed to broadcast via localStorage', error);
        }
      }
    }
  }

  /**
   * Subscribe to cart state changes
   *
   * @param {Function} callback - Called with (newState, oldState) when state changes
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this.subscribers.add(callback);

    // Return unsubscribe function
    return () => this.subscribers.delete(callback);
  }

  /**
   * Get current cart state
   * @returns {Object} Current cart state
   */
  getState() {
    return { ...this.state };
  }

  /**
   * Dispatch cart error event for global error handling
   * @param {String} message - Error message
   */
  dispatchError(message) {
    const errorEvent = new CustomEvent('cart:error', {
      detail: { message },
      bubbles: true
    });
    document.dispatchEvent(errorEvent);
  }

  /**
   * Build an error for a failed cart mutation. A 422 is Shopify rejecting the
   * change against a cart rule (e.g. the subscription minimum), so it carries
   * a message meant for the shopper and must not be retried.
   *
   * @param {String} message - Message from the cart API
   * @param {Number} status - HTTP status of the failed response
   * @returns {Error}
   */
  cartError(message, status) {
    const error = new Error(message);
    error.status = status;
    error.isCartRuleError = status === 422;
    return error;
  }

  // ========================================
  // Cart API Methods (all queued for serial execution)
  // ========================================

  /**
   * Update quantity of a line item. Accepts a line item key or a numeric
   * 1-based line index. Keys are preferred because Shopify re-numbers lines
   * after every removal.
   *
   * @param {String|Number} target - Line item key (string) or line index (number)
   * @param {Number} quantity - New quantity (0 to remove)
   * @returns {Promise<Object>} Updated cart data
   */
  async updateQuantity(target, quantity) {
    const isKey = typeof target === 'string' && target.indexOf(':') !== -1;
    const originalVariantId = isKey ? target.split(':')[0] : null;

    return this.queueRequest(async () => {
      const makeBody = (id) => JSON.stringify(
        isKey ? { id, quantity: parseInt(quantity) }
              : { line: parseInt(target), quantity: parseInt(quantity) }
      );

      let response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: makeBody(target)
      });

      // Shopify regenerates line item keys after every cart mutation. If the
      // key captured at click time is stale by the time this queued request
      // runs, retry once with the current key for the same variant.
      if (!response.ok && isKey) {
        const fresh = await this.fetchCart();
        const match = fresh.items.find((it) => String(it.variant_id) === originalVariantId);
        if (!match) {
          // Variant no longer in cart — treat as success.
          this.setState(fresh);
          return fresh;
        }
        response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: makeBody(match.key)
        });
      }

      if (!response.ok) {
        const error = await response.json();
        const msg = `${error?.message || error?.description || 'Failed to update quantity'}`;
        throw this.cartError(msg, response.status);
      }

      return await response.json();
    });
  }

  /**
   * Add item(s) to cart
   *
   * @param {Number|String} id - Variant ID
   * @param {Number} quantity - Quantity to add
   * @param {Object} properties - Line item properties
   * @param {String} sellingPlan - Selling plan ID (for subscriptions)
   * @returns {Promise<Object>} Updated cart data
   */
  async addItem(id, quantity = 1, properties = {}, sellingPlan = null) {
    return this.queueRequest(async () => {
      const item = {
        id: String(id),
        quantity: parseInt(quantity)
      };

      if (properties && Object.keys(properties).length > 0) {
        item.properties = properties;
      }

      if (sellingPlan) {
        item.selling_plan = sellingPlan;
      }

      const body = JSON.stringify({ items: [item] });

      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || error.description || 'Failed to add item to cart');
      }

      return await response.json();
    });
  }

  /**
   * Add multiple items to cart in a single request
   *
   * @param {Array} items - Array of {id, quantity, properties, selling_plan}
   * @returns {Promise<Object>} Response from cart/add.js
   */
  async addItems(items) {
    return this.queueRequest(async () => {
      const body = JSON.stringify({
        items: items.map(item => {
          const entry = {
            id: String(item.id),
            quantity: parseInt(item.quantity) || 1
          };
          if (item.properties && Object.keys(item.properties).length > 0) {
            entry.properties = item.properties;
          }
          if (item.selling_plan) {
            entry.selling_plan = item.selling_plan;
          }
          return entry;
        })
      });

      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || error.description || 'Failed to add items to cart');
      }

      return await response.json();
    });
  }

  /**
   * Update multiple line items at once
   *
   * @param {Object} updates - Object mapping variant ID to quantity
   * @returns {Promise<Object>} Updated cart data
   */
  async updateCart(updates) {
    return this.queueRequest(async () => {
      const body = JSON.stringify({ updates });

      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body
      });

      if (!response.ok) {
        const error = await response.json();
        throw this.cartError(
          error.message || error.description || 'Failed to update cart',
          response.status
        );
      }

      return await response.json();
    });
  }

  /**
   * Clear all items from cart
   * @returns {Promise<Object>} Empty cart data
   */
  async clearCart() {
    return this.queueRequest(async () => {
      const response = await fetch(window.Shopify.routes.root + 'cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to clear cart');
      }

      return await response.json();
    });
  }
}

// Create and export singleton instance
window.cartStateManager = new CartStateManager();
